import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  Space,
  Select,
  Input,
  Table,
  Tag,
  Card,
  Alert,
  Tabs,
  Switch,
  Popconfirm,
  Modal,
  Radio,
  Empty,
} from "antd";
import {
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ExperimentOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import {
  getDeviceScopeSettings,
  saveDeviceScopeSettings,
  previewDeviceScopeSettings,
  getDeviceClassification,
  setCameraLifecycle,
} from "../network/api";
import {
  LIFECYCLE,
  LIFECYCLE_LABEL,
  LIFECYCLE_TAG_COLOR,
  relativeTime,
} from "../constants/health";

const { Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// In-house criteria — which devices are still our own kit.
//
// This is the setting that decides the denominator on every fleet number, so the
// page leads with what the rules currently do to the live fleet and never saves
// without showing which devices the change moves.
// ---------------------------------------------------------------------------
// "is set" / "is not set" carry no value, so an empty value box is fine for them.
const VALUELESS_OPS = ["isSet", "isNotSet"];
const isValueless = (op) => VALUELESS_OPS.includes(op);
const usableRules = (rules) =>
  rules.filter((r) => isValueless(r.op) || r.value?.trim());

const DeviceScopeRules = () => {
  const [rules, setRules] = useState([]);
  const [matchMode, setMatchMode] = useState("any");
  const [catalog, setCatalog] = useState({ fields: [], ops: [], matchModes: [] });
  const [meta, setMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeviceScopeSettings();
      setRules(res.inHouseRules || []);
      setMatchMode(res.matchMode || "any");
      setCatalog(res.catalog || { fields: [], ops: [], matchModes: [] });
      setMeta({ updatedAt: res.updatedAt, updatedBy: res.updatedBy });
      setDirty(false);
      // Show the effect of the SAVED rules straight away — the page is useless
      // without "and this is what it currently means".
      setPreview(
        await previewDeviceScopeSettings(res.inHouseRules || [], res.matchMode || "any")
      );
    } catch (e) {
      notify.error(serverError(e, "Could not load the in-house criteria."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = (next) => {
    setRules(next);
    setDirty(true);
    setPreview(null); // a stale preview is worse than none
  };

  const addRule = () =>
    mutate([...rules, { field: "publicIp", op: "equals", value: "", label: "", enabled: true }]);

  const patch = (i, changes) =>
    mutate(rules.map((r, idx) => (idx === i ? { ...r, ...changes } : r)));

  const runPreview = async () => {
    try {
      setPreview(await previewDeviceScopeSettings(usableRules(rules), matchMode));
    } catch (e) {
      notify.error(serverError(e, "Could not preview these criteria."));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await saveDeviceScopeSettings(usableRules(rules), matchMode);
      setRules(res.inHouseRules || []);
      setMatchMode(res.matchMode || "any");
      setPreview(res.preview || null);
      setMeta({ updatedAt: res.updatedAt, updatedBy: meta?.updatedBy });
      setDirty(false);
      notify.success("In-house criteria saved. The next health sweep applies them.");
    } catch (e) {
      notify.error(serverError(e, "Could not save the criteria."));
    } finally {
      setSaving(false);
    }
  };

  const hintFor = (field) => catalog.fields.find((f) => f.value === field)?.hint;

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="What counts as “still in our facility”"
        description={
          <>
            A device matching the criteria below is treated as QC / bench: it is reported
            separately and never counts toward fleet health, however long it stays silent.
            Everything else is treated as installed at a site.
            <br />
            Add a rule for each way you recognise your own kit — the office WAN, a dedicated QC
            configuration, a test SSID, or “outlet is not set”. A configuration rule is the
            reliable one for QC runs done away from the office, because it follows the device
            rather than the network.
            <br />
            <strong>Careful with “outlet is not set” on its own:</strong> plenty of genuinely
            installed devices were never bound to an outlet, and an ANY rule would hide them
            from the board too. Combine it with a location rule using <strong>ALL</strong> —
            e.g. <em>outlet is not set</em> AND <em>public IP is the office</em>. The preview
            below always shows exactly who moves.
          </>
        }
      />

      <Card
        size="small"
        title="In-house criteria"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <Button icon={<PlusOutlined />} onClick={addRule}>
              Add rule
            </Button>
            <Button icon={<ExperimentOutlined />} onClick={runPreview} disabled={!rules.length}>
              Preview effect
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={!dirty}
              onClick={save}
            >
              Save
            </Button>
          </Space>
        }
      >
        {/* How the rules combine, stated as a sentence rather than left implied.
            ANY and ALL give opposite answers, so this is never a hidden default. */}
        <Space align="center" wrap style={{ marginBottom: 14 }}>
          <Text>A device is treated as in-house when it</Text>
          <Select
            style={{ width: 320 }}
            value={matchMode}
            options={
              catalog.matchModes?.length
                ? catalog.matchModes
                : [
                    { value: "any", label: "matches ANY of these rules (OR)" },
                    { value: "all", label: "matches ALL of these rules (AND)" },
                  ]
            }
            onChange={(v) => {
              setMatchMode(v);
              setDirty(true);
              setPreview(null);
            }}
          />
        </Space>

        {matchMode === "all" && rules.filter((r) => r.enabled !== false).length < 2 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="ALL with fewer than two active rules behaves the same as ANY."
          />
        )}

        {!rules.length && !loading && (
          <Empty
            description="No criteria — every device that reports from anywhere will be counted as deployed."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        <Space direction="vertical" style={{ width: "100%" }} size={10}>
          {rules.map((r, i) => (
            <Space key={i} wrap align="start">
              <Switch
                checked={r.enabled !== false}
                onChange={(v) => patch(i, { enabled: v })}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
              <Select
                style={{ width: 190 }}
                value={r.field}
                options={catalog.fields}
                onChange={(v) => patch(i, { field: v })}
              />
              <Select
                style={{ width: 140 }}
                value={r.op || "equals"}
                options={catalog.ops}
                onChange={(v) => patch(i, { op: v })}
              />
              <Input
                style={{ width: 240 }}
                placeholder={isValueless(r.op) ? "— not needed —" : "value"}
                disabled={isValueless(r.op)}
                value={isValueless(r.op) ? "" : r.value}
                onChange={(e) => patch(i, { value: e.target.value })}
              />
              <Input
                style={{ width: 220 }}
                placeholder="label (optional, e.g. Head-office WAN)"
                value={r.label}
                onChange={(e) => patch(i, { label: e.target.value })}
              />
              <Popconfirm
                title="Remove this rule?"
                onConfirm={() => mutate(rules.filter((_, idx) => idx !== i))}
              >
                <Button danger icon={<DeleteOutlined />} />
              </Popconfirm>
              {hintFor(r.field) && (
                <Text type="secondary" style={{ fontSize: 12, maxWidth: 320, display: "block" }}>
                  {hintFor(r.field)}
                </Text>
              )}
            </Space>
          ))}
        </Space>

        {meta?.updatedAt && (
          <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            Last changed {relativeTime(meta.updatedAt)}
            {meta.updatedBy?.name ? ` by ${meta.updatedBy.name}` : ""}.
          </Paragraph>
        )}
      </Card>

      {dirty && !preview && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Unsaved changes — preview them before saving"
          description="These criteria decide which devices are counted at all. Click Preview effect to see exactly which devices would move."
        />
      )}

      {preview && (
        <Card size="small" title="Effect on the live fleet">
          <Space size={24} wrap style={{ marginBottom: 12 }}>
            <span>
              <Text type="secondary">Would be in-house (QC): </Text>
              <Text strong>{preview.inHouse}</Text>
            </span>
            <span>
              <Text type="secondary">Would be deployed: </Text>
              <Text strong>{preview.deployed}</Text>
            </span>
            <span>
              <Text type="secondary">Devices that move: </Text>
              <Text strong type={preview.changingCount ? "warning" : undefined}>
                {preview.changingCount}
              </Text>
            </span>
          </Space>

          {preview.changingCount > 0 ? (
            <Table
              size="small"
              rowKey="deviceId"
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              dataSource={preview.changing}
              columns={[
                { title: "Device", dataIndex: "deviceId" },
                { title: "Public IP", dataIndex: "publicIp", render: (v) => v || "—" },
                { title: "SSID", dataIndex: "currentNetwork", render: (v) => v || "—" },
                { title: "Configuration", dataIndex: "configTitle", render: (v) => v || "—" },
                {
                  title: "Outlet",
                  dataIndex: "outlet",
                  render: (v) => v || <Text type="secondary">unbound</Text>,
                },
                {
                  title: "Change",
                  render: (_, r) => (
                    <Space>
                      <Tag color={LIFECYCLE_TAG_COLOR[r.currently]}>
                        {LIFECYCLE_LABEL[r.currently] || r.currently || "—"}
                      </Tag>
                      <span>→</span>
                      <Tag color={LIFECYCLE_TAG_COLOR[r.wouldBe]}>
                        {LIFECYCLE_LABEL[r.wouldBe] || r.wouldBe}
                      </Tag>
                    </Space>
                  ),
                },
                {
                  title: "Matched rules",
                  dataIndex: "matched",
                  render: (m) =>
                    m?.length ? (
                      <Text type="secondary">
                        {m
                          .map((x) => x.label || `${x.field} ${x.op}${x.value ? ` ${x.value}` : ""}`)
                          .join(" + ")}
                      </Text>
                    ) : (
                      <Text type="secondary">—</Text>
                    ),
                },
              ]}
            />
          ) : (
            <Text type="secondary">No device changes classification with these criteria.</Text>
          )}
        </Card>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Per-device override — mark one unit as QC / Maintenance regardless of the rules.
// Deliberately lives here rather than on the Devices page: it changes what the
// fleet numbers mean, so it belongs with the other settings, behind the same gate.
// ---------------------------------------------------------------------------
const DeviceClassification = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState();
  const [editing, setEditing] = useState(null); // the device row being marked
  const [pinValue, setPinValue] = useState(LIFECYCLE.QC);
  const [pinReason, setPinReason] = useState("");
  const [savingPin, setSavingPin] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeviceClassification({ lifecycle: filter, search: search || undefined });
      setDevices(res.devices || []);
    } catch (e) {
      notify.error(serverError(e, "Could not load device classification."));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const openEditor = (row) => {
    setEditing(row);
    setPinValue(row.lifecycle?.pinnedValue || row.lifecycle?.value || LIFECYCLE.QC);
    setPinReason(row.lifecycle?.pinnedReason || "");
  };

  const applyPin = async (clear = false) => {
    setSavingPin(true);
    try {
      await setCameraLifecycle(editing._id, {
        value: clear ? null : pinValue,
        reason: clear ? undefined : pinReason,
      });
      notify.success(
        clear
          ? `${editing.deviceId} returned to automatic classification.`
          : `${editing.deviceId} marked ${LIFECYCLE_LABEL[pinValue]}.`
      );
      setEditing(null);
      load();
    } catch (e) {
      notify.error(serverError(e, "Could not update this device."));
    } finally {
      setSavingPin(false);
    }
  };

  const columns = [
    { title: "Device ID", dataIndex: "deviceId" },
    { title: "Title", dataIndex: "deviceTitle", render: (v) => v || <Text type="secondary">—</Text> },
    {
      title: "Classification",
      render: (_, r) => (
        <Space>
          <Tag color={LIFECYCLE_TAG_COLOR[r.lifecycle?.value]}>
            {LIFECYCLE_LABEL[r.lifecycle?.value] || r.lifecycle?.value || "—"}
          </Tag>
          {r.lifecycle?.source === "manual" && <Tag color="purple">pinned</Tag>}
        </Space>
      ),
    },
    {
      title: "Why",
      dataIndex: ["lifecycle", "reason"],
      render: (v) => <Text type="secondary">{v || "—"}</Text>,
    },
    { title: "Public IP", dataIndex: "publicIp", render: (v) => v || "—" },
    { title: "SSID", dataIndex: "currentNetwork", render: (v) => v || "—" },
    { title: "Configuration", dataIndex: "configTitle", render: (v) => v || "—" },
    {
      title: "Outlet",
      dataIndex: "shop",
      render: (s) => (s ? s.title || s.code : <Text type="secondary">unbound</Text>),
    },
    { title: "Last seen", dataIndex: "lastSeenAt", render: (v) => relativeTime(v) },
    {
      title: "",
      render: (_, r) => (
        <Button size="small" onClick={() => openEditor(r)}>
          Mark…
        </Button>
      ),
    },
  ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Override the automatic classification for one device"
        description="Use this when the criteria get a device wrong — a unit you are QC'ing on a network we do not recognise, or one pulled from a shop for repair. A pin always beats the rules and stays until you clear it."
        />

      <Space style={{ marginBottom: 12 }} wrap>
        <Input.Search
          allowClear
          placeholder="Search by device ID or title"
          style={{ width: 280 }}
          onSearch={setSearch}
        />
        <Select
          allowClear
          placeholder="Filter by classification"
          style={{ width: 200 }}
          value={filter}
          onChange={setFilter}
          options={[
            { value: LIFECYCLE.DEPLOYED, label: LIFECYCLE_LABEL.DEPLOYED },
            { value: LIFECYCLE.QC, label: LIFECYCLE_LABEL.QC },
            { value: LIFECYCLE.MAINTENANCE, label: LIFECYCLE_LABEL.MAINTENANCE },
          ]}
        />
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      </Space>

      <Table
        size="small"
        rowKey="deviceId"
        loading={loading}
        columns={columns}
        dataSource={devices}
        scroll={{ x: 1200 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />

      <Modal
        open={!!editing}
        title={`Mark ${editing?.deviceId || ""}`}
        onCancel={() => setEditing(null)}
        footer={[
          editing?.lifecycle?.pinnedValue && (
            <Button key="clear" danger loading={savingPin} onClick={() => applyPin(true)}>
              Clear pin (back to automatic)
            </Button>
          ),
          <Button key="cancel" onClick={() => setEditing(null)}>
            Cancel
          </Button>,
          <Button key="ok" type="primary" loading={savingPin} onClick={() => applyPin(false)}>
            Save
          </Button>,
        ].filter(Boolean)}
      >
        <Radio.Group
          value={pinValue}
          onChange={(e) => setPinValue(e.target.value)}
          style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}
        >
          <Radio value={LIFECYCLE.QC}>
            <strong>{LIFECYCLE_LABEL.QC}</strong> — in our facility. Not counted in fleet health.
          </Radio>
          <Radio value={LIFECYCLE.MAINTENANCE}>
            <strong>{LIFECYCLE_LABEL.MAINTENANCE}</strong> — pulled from service, expected back.
            Not counted.
          </Radio>
          <Radio value={LIFECYCLE.DEPLOYED}>
            <strong>{LIFECYCLE_LABEL.DEPLOYED}</strong> — installed at a site. Counted, and will
            show red when it stops reporting.
          </Radio>
        </Radio.Group>
        <Input.TextArea
          rows={2}
          placeholder="Reason (optional) — e.g. on the bench for door-sensor testing"
          value={pinReason}
          onChange={(e) => setPinReason(e.target.value)}
        />
      </Modal>
    </>
  );
};

const Settings = () => {
  const { isHardwareAdmin } = useAuth();

  if (!isHardwareAdmin) {
    return (
      <Alert
        type="error"
        showIcon
        message="Not available"
        description="Settings are restricted to hardware admins and above."
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Fleet-wide configuration — hardware admins and above"
        icon={<SettingOutlined />}
      />
      <Tabs
        defaultActiveKey="device-scope"
        items={[
          {
            key: "device-scope",
            label: "In-house criteria",
            children: <DeviceScopeRules />,
          },
          {
            key: "classification",
            label: "Device classification",
            children: <DeviceClassification />,
          },
        ]}
      />
    </div>
  );
};

export default Settings;
